/**
 * MouldTrack domain types - canonical TypeScript mirror of the Prisma data model
 * (see doc/04-data-model.md). Shared by the API, admin dashboard, and mobile PWA.
 *
 * These string-literal unions intentionally match the Prisma enums one-for-one.
 */
// --- Enums (mirror prisma/schema.prisma) ---
export const AssetType = ['MOULD', 'DIE', 'TOOL', 'FIXTURE'];
/**
 * What each status is called in front of a person.
 *
 * The stored values are database words - IN_STORE, UNDER_MAINTENANCE - and were
 * reaching the shop floor unchanged on the scanner's result card, underscores
 * and all. The wording lived in five separate files, which is how the detail
 * sheet came to say "In store" while the card behind it said "IN_STORE" about
 * the same tool at the same moment.
 */
export const MOULD_STATUS_LABEL = {
    IN_STORE: 'In store',
    ISSUED: 'Checked out',
    IN_TRANSIT: 'In transit',
    AT_VENDOR: 'At vendor',
    UNDER_MAINTENANCE: 'Under maintenance',
    SCRAPPED: 'Scrapped',
};
/** The status in words, falling back to whatever the server sent. */
export function mouldStatusLabel(status) {
    if (!status)
        return '';
    return MOULD_STATUS_LABEL[status] ?? status;
}
/** A Mold OUT: the kind of scan that starts a trip and puts a mould in transit. */
export function isCheckOut(checkpointType) {
    return checkpointType === 'STORE_OUT' || checkpointType === 'GATE_OUT';
}
/**
 * Where a mould in transit was last recorded, in words - or null if nowhere.
 *
 * Between a Mold OUT and the Mold IN at its destination, the place of its
 * latest scan is not where the mould is. Straight after the check-out it is
 * the site it left ("from Mira Road"); recorded again on the way, it is where
 * it was seen ("last seen at Andheri"). The portal and the Scanner both word
 * it here, so they say the same thing. When the kind of the latest scan is not
 * known, "last seen at" is the wording that is always true.
 */
export function transitOrigin(place, lastCheckpointType) {
    const at = place?.trim();
    if (!at)
        return null;
    return isCheckOut(lastCheckpointType) ? `from ${at}` : `last seen at ${at}`;
}
export const MouldStatus = [
    'IN_STORE',
    'ISSUED',
    'IN_TRANSIT',
    'AT_VENDOR',
    'UNDER_MAINTENANCE',
    'SCRAPPED',
];
export const LocationType = ['PLANT', 'WAREHOUSE', 'VENDOR', 'CUSTOMER'];
export const CheckpointType = [
    'STORE_IN',
    'STORE_OUT',
    'GATE_OUT',
    'GATE_IN',
    'TRANSIT',
    'QC',
    'MAINTENANCE',
    'LOCATION', // v2: plain "where is it now" scan point
];
export const TransferStatus = ['DRAFT', 'IN_TRANSIT', 'COMPLETED', 'CANCELLED'];
export const UserRole = [
    'ADMIN',
    'STORE_KEEPER', // v1
    'GATE_SECURITY', // v1
    'VIEWER',
    'INVENTORY_MANAGER', // v2: manages catalog + master data
    'LOCATION_SUPERVISOR', // v2: scans moulds to record their location
];
/** v2 master-table entity names exposed by the generic /masters CRUD. */
export const MasterEntity = [
    'companies',
    'projects',
    'designers',
    'tool-types',
    'runner-types',
    'gate-types',
    'ejection-types',
    'materials',
    'material-grades',
    'mechanism-types',
    'nameplate-templates',
];
export const JOURNEY_STATUS_LABEL = {
    PLANNED: 'Planned',
    IN_PROGRESS: 'On its way',
    COMPLETED: 'Arrived',
    CANCELLED: 'Cancelled',
};
// --- Maintenance and usage (v4) ---
export const MaintenanceType = [
    'PREVENTIVE',
    'BREAKDOWN',
    'MODIFICATION',
    'REFURBISHMENT',
    'INSPECTION',
];
export const MaintenanceStatus = [
    'OPEN',
    'IN_PROGRESS',
    'ON_HOLD',
    'COMPLETED',
    'CANCELLED',
];
export const StockTakeStatus = ['OPEN', 'CLOSED', 'ABANDONED'];
//# sourceMappingURL=types.js.map